import Effects from 'Effects';
import PolyParameter from 'parameters/PolyParameter.js';
import {bulkUpdate} from 'utils/bulkUpdate.js';
import Tone from 'tone';
import {AudioParamPercentageValueParameter,
        AudioParamValueParameter} from 'parameters/AbstractAudioParamParameter.js';
import HasTrack from './HasTrack.js';

const NUM_COMPONENTS = 10;

const FREQUENCIES = [
    90,
    250,
    400,
    1250,
    1950,
    2350,
    2750,
    5500,
    6900,
    8600,
];
        
export default class VocoderEffect extends HasTrack(Effects.Effect) {
    constructor(sequencerEngine) {
        super('Vocoder', () => new Tone.Gain(4), sequencerEngine); 
        
        this.components = [];

        this.outGain = new Tone.Gain(16);
        for (let i=0; i < NUM_COMPONENTS; i++) {
            let frequency = FREQUENCIES[i];
            let filterIn = new Tone.Filter(frequency, 'bandpass');
            let filterOut = new Tone.Filter(frequency, 'bandpass');
            let componentGain = new Tone.Gain(1);
            filterIn.Q.value = 50;
            filterOut.Q.value = 50;
            let follower = new Tone.Follower(0.01);
            filterIn.chain(follower, componentGain.gain);
            this.effectNode.chain(componentGain, filterOut, this.outGain);
            this.components.push({
                filterIn,
                filterOut,
                follower
            });
        };

        this.setupTrackParameter();

        this.qParameter = new PolyParameter(
            'q',
            [... this.components.map(c => c.filterIn.Q),
             ... this.components.map(c => c.filterOut.Q)],
            {
                defaultValue: 50,
                min: 0.1,
                max: 100,
            });

        this.registerParameter(this.qParameter);

        this.roughnessParameter = new PolyParameter(
            'roughness',
            this.components.map(c => c.follower._filter.frequency),
            {
                defaultValue: 20,
                min: 0.1,
                max: 100,
            });

        this.registerParameter(this.roughnessParameter);

        this.frequencyParameter = new PolyParameter(
            'frequency',
            [... this.components.map(c => c.filterIn.frequency),
             ... this.components.map(c => c.filterOut.frequency)],
            {
                defaultValue: 0,
                min: 0,
                max: 4000,
            });

        this.registerParameter(this.frequencyParameter);

        this.components.forEach(
            ({filterIn, filterOut}, i) => {
                filterIn.frequency.value = FREQUENCIES[i];
                filterOut.frequency.value = FREQUENCIES[i];
            });
        this.updateInitialValues();
    }
  
    dispose() {
        super.dispose();
        this.components.forEach((
            {filterIn, filterOut, follower}) => {
                filterIn.dispose();
                filterOut.dispose();
                follower.dispose();
            });

        this.outGain.dispose();
    }

    connect(node) {
        this.outGain.connect(node);
    }

    disconnect(node) {
        this.outGain.disconnect();
    }

    onChangeTrack(track) {
        if (track === -1) {
            this._track = undefined;
            return;
        }
        track = this.sequencerEngine.tracks[track];
        if (this._track) {
            this.disconnectFromTrack(this._track);
        }

        this.connectToTrack(track);
        super.onChangeTrack(track);
    }

    disconnectFromTrack(track) {
        for (let {filterIn} of this.components) {
            track.trackNode.disconnect(filterIn);
        }
    }

    connectToTrack(track) {
        for (let {filterIn} of this.components) {
            track.trackNode.connect(filterIn);
        }
    }

    getJson(isPreset) {
        let json = {
            ... super.getJson(isPreset),
            q: this.qParameter.get(true),
            roughness: this.roughnessParameter.get(true),
            frequency: this.frequencyParameter.get(true),
            track: this.trackParameter.get(true)
        };

        json = this.saveParameterToJson(json, 'frequencyParameter', this.frequencyParameter);
        json = this.saveParameterToJson(json, 'qParameter', this.qParameter);
        json = this.saveParameterToJson(json, 'roughnessParameter', this.roughnessParameter);
        return json;
    }

    fromJson(json, isCopy) {
        let bulk = super.fromJson(json, isCopy);
        bulkUpdate(this.qParameter, json.q, bulk);
        bulkUpdate(this.roughnessParameter, json.roughness, bulk);
        bulkUpdate(this.frequencyParameter, json.frequency, bulk);
        bulkUpdate(this.trackParameter, json.track, bulk);

        return {
            ... bulk,
            ... this.loadParameterFromJson(json, 'frequencyParameter', this.frequencyParameter),
            ... this.loadParameterFromJson(json, 'qParameter', this.qParameter),
            ... this.loadParameterFromJson(json, 'roughnessParameter', this.roughnessParameter),
        };
    }

    getEffectCopy(sequencerEngine, ignoreId=true) {
        let fx = new VocoderEffect(sequencerEngine);
        fx.fromJson(this.getJson(), ignoreId);
        return true;
    }
}

 
